import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { RefreshCw, Lock, Unlock, LogOut, ShieldAlert } from "lucide-react";
import { IsolationTestPanel, FileAccessPanel } from "../components/SecurityFilePanels";
import { MediaRetentionPanel } from "../components/MediaRetentionPanel";

const EVENT_LABEL = {
  login_ok: { label: "Accesso riuscito", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  login_fail: { label: "Accesso fallito", cls: "bg-red-50 text-red-700 border-red-200" },
  login_locked: { label: "Tentativo su account bloccato", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  logout: { label: "Logout", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  register: { label: "Registrazione", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  password_reset: { label: "Password reimpostata", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  admin_unlock: { label: "Sblocco admin", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  admin_force_logout: { label: "Logout forzato", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

function StatCard({ label, value, tone = "neutral", testId }) {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50"
      : tone === "bad"
      ? "border-red-200 bg-red-50"
      : "border-[var(--mp-border,#e5e7eb)] bg-white";
  return (
    <div data-testid={testId} className={`rounded-xl border ${toneCls} p-4`}>
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-[var(--mp-ink,#111)]">{value}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminSecurity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/security/overview", {
        params: { q: q || undefined, type: type || undefined, limit: 200 },
      });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, type]);

  useEffect(() => {
    load();
  }, [load]);

  const unlock = async (email) => {
    try {
      await api.post("/admin/security/unlock", { email });
      toast.success(`Account ${email} sbloccato`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const forceLogout = async (ev) => {
    if (!ev.user_id) return;
    if (!window.confirm(`Disconnettere ${ev.email} da tutti i dispositivi?`)) return;
    try {
      await api.post(`/admin/security/force-logout/${ev.user_id}`);
      toast.success(`${ev.email} disconnesso ovunque`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const s = data?.last_24h || {};
  const lockouts = data?.active_lockouts || [];
  const events = data?.events || [];

  return (
    <div className="space-y-6" data-testid="admin-security-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--mp-ink,#111)] flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" /> Sicurezza
          </h1>
          <p className="text-sm text-neutral-500">
            Accessi, tentativi falliti e blocchi delle ultime 24 ore. Il log viene conservato 90 giorni.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="security-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Accessi ok" value={s.login_ok ?? "—"} tone="good" testId="stat-login-ok" />
        <StatCard label="Accessi falliti" value={s.login_fail ?? "—"} tone={s.login_fail > 20 ? "bad" : "neutral"} testId="stat-login-fail" />
        <StatCard label="IP sospetti" value={s.distinct_fail_ips ?? "—"} tone={s.distinct_fail_ips > 5 ? "warn" : "neutral"} testId="stat-fail-ips" />
        <StatCard label="Account bloccati" value={lockouts.length} tone={lockouts.length ? "bad" : "neutral"} testId="stat-lockouts" />
        <StatCard label="Reset password" value={s.password_reset ?? "—"} testId="stat-password-reset" />
        <StatCard label="Registrazioni" value={s.register ?? "—"} testId="stat-register" />
      </div>

      {lockouts.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="lockouts-panel">
          <div className="font-semibold text-red-800 flex items-center gap-2 mb-2">
            <Lock className="h-4 w-4" /> Account temporaneamente bloccati
          </div>
          <ul className="space-y-2">
            {lockouts.map((l) => (
              <li key={l.email} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`lockout-${l.email}`}>
                <span>
                  <span className="font-medium">{l.email}</span>
                  <span className="text-neutral-600"> · {l.fails} tentativi · fino alle {fmt(l.locked_until)}</span>
                </span>
                <Button size="sm" variant="outline" onClick={() => unlock(l.email)} data-testid={`unlock-${l.email}`}>
                  <Unlock className="h-3.5 w-3.5 mr-1" /> Sblocca
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <IsolationTestPanel />

      <FileAccessPanel />

      <MediaRetentionPanel />

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Cerca email o IP…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="security-search"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm"
          data-testid="security-type-filter"
        >
          <option value="">Tutti gli eventi</option>
          {Object.entries(EVENT_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[var(--mp-border,#e5e7eb)] bg-white">
        <table className="w-full text-sm" data-testid="security-events-table">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-3 py-2">Quando</th>
              <th className="px-3 py-2">Evento</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Ruolo</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">Dispositivo</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-neutral-500">
                  Nessun evento registrato.
                </td>
              </tr>
            )}
            {events.map((ev) => {
              const meta = EVENT_LABEL[ev.type] || { label: ev.type, cls: "bg-neutral-100 text-neutral-700 border-neutral-200" };
              return (
                <tr key={ev.id} className="border-t border-neutral-100" data-testid={`event-${ev.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{fmt(ev.at)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-3 py-2 font-medium">{ev.email || "—"}</td>
                  <td className="px-3 py-2 text-neutral-600">{ev.role || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">{ev.ip || "—"}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500 max-w-[260px] truncate" title={ev.user_agent || ""}>
                    {ev.user_agent || "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {ev.user_id && ev.type === "login_ok" && (
                      <Button size="sm" variant="ghost" onClick={() => forceLogout(ev)} title="Disconnetti da tutti i dispositivi" data-testid={`force-logout-${ev.id}`}>
                        <LogOut className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
